/**
 * Create Or Edit Table Tool Schema
 * 
 * Create a new table or edit an existing table on a slide.
 * Uses rowOperations and columnOperations for structural changes.
 * Cell text uses paragraphOperations[].
 */

import { z } from "zod";

// Import shared schemas
import {
    PositionSchema,
    TextFrameSchema,
    CellRefSchema,
    CellRangeSchema,
    BorderSchema,
} from "../../../tools/liveBuildArea/liveBuildArea.schema";

import {
    TextContentWithOperationsSchema,
    MetadataOperationsSchema,
} from "../createOrEditShape/createOrEditShape.schema";

// ═══════════════════════════════════════════════════════════════════════════
// ROW/COLUMN OPERATIONS SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Row operation schema
 */
export const RowOperationSchema = z.object({
    operation: z.enum(["insert", "remove"])
        .describe("Operation type"),
    
    index: z.number().int().min(1)
        .describe("Row index (1-based). For insert, this refers to the anchor row."),
    
    insertPosition: z.enum(["at", "after"]).optional()
        .describe("For insert only: 'at' inserts before the anchor row, 'after' inserts after it."),
    
    count: z.number().int().min(1).optional()
        .describe("Number of rows to insert (INSERT ONLY, default 1)"),
    
    inheritFormattingFrom: z.object({
        row: z.number().int().min(1),
        properties: z.array(z.enum(["fill", "borders", "textFormat", "all"])).optional(),
    }).optional()
        .describe("For INSERT: inherit formatting from this row"),
    
    spacingConsequences: z.string().optional()
        .describe("Impact: 'Table height increases by ~30pt, logo below needs to move down'"),
    
    shapesAffectedBySpacingConsequences: z.array(z.number().int()).optional()
        .describe("Shape IDs affected by table height changes"),
}).superRefine((data, ctx) => {
    if (data.operation === "insert" && !data.insertPosition) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "insertPosition is required for insert operations.",
        });
    }
});

/**
 * Column operation schema
 */
export const ColumnOperationSchema = z.object({
    operation: z.enum(["insert", "remove"])
        .describe("Operation type"),
    
    index: z.number().int().min(1)
        .describe("Column index (1-based). For insert, this refers to the anchor column."),
    
    insertPosition: z.enum(["at", "after"]).optional()
        .describe("For insert only: 'at' inserts before the anchor column, 'after' inserts after it."),
    
    count: z.number().int().min(1).optional()
        .describe("Number of columns to insert (INSERT ONLY, default 1)"),
    
    inheritFormattingFrom: z.object({
        column: z.number().int().min(1),
        properties: z.array(z.enum(["fill", "borders", "textFormat", "all"])).optional(),
    }).optional()
        .describe("For INSERT: inherit formatting from this column"),
    
    spacingConsequences: z.string().optional()
        .describe("Impact: 'Table width increases by ~80pt, may need to resize or shift shapes'"),
    
    shapesAffectedBySpacingConsequences: z.array(z.number().int()).optional()
        .describe("Shape IDs affected by table width changes"),
}).superRefine((data, ctx) => {
    if (data.operation === "insert" && !data.insertPosition) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "insertPosition is required for insert operations.",
        });
    }
});

// ═══════════════════════════════════════════════════════════════════════════
// CELL SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Cell text entry with operations
 */
export const CellTextWithOperationsSchema = z.object({
    cell: CellRefSchema
        .describe("Cell reference (row, col) - 1-based"),
    
    textFrame: TextFrameSchema.optional()
        .describe("Text frame properties for this cell (defaults: only row 1/col 1 center+middle, others left+middle; override for headers, short text, numbers, or long text)"),
    
    textContent: TextContentWithOperationsSchema.optional()
        .describe("Text content with paragraphOperations[]"),
});

/**
 * Cell formatting schema
 */
export const CellFormattingSchema = z.object({
    cellRange: CellRangeSchema
        .describe("Range of cells to format (1-based, inclusive)"),
    
    copyFormatFrom: z.object({
        componentId: z.string().optional(),
        slideId: z.number().int().optional(),
        tableShapeId: z.number().int(),
        sourceCell: CellRefSchema.optional(),
        sourceCellRange: CellRangeSchema.optional(),
        properties: z.array(z.enum([
            "fill", "borderTop", "borderBottom", "borderLeft", "borderRight", "borders"
        ])).optional(),
    }).optional()
        .describe("Copy formatting from a source table cell/range (not from the current table unless referenced)"),
    
    explicitFormatting: z.object({
        fill: z.object({
            type: z.literal("solid"),
            color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
            transparency: z.number().min(0).max(1).optional(),
            null: z.boolean().optional(),
        }).optional(),
        borderTop: BorderSchema.optional(),
        borderBottom: BorderSchema.optional(),
        borderLeft: BorderSchema.optional(),
        borderRight: BorderSchema.optional(),
    }).optional()
        .describe("Explicit formatting values (defaults: transparent fill, no borders, black text)"),
});

// ═══════════════════════════════════════════════════════════════════════════
// COPY STYLE FROM SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

const ColorOverrideSchema = z.object({
    from: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Must be hex color #RRGGBB")
        .describe("Original color to replace"),
    to: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Must be hex color #RRGGBB")
        .describe("New color to use"),
});

const FontOverrideSchema = z.object({
    name: z.string().optional()
        .describe("Font family name override"),
    size: z.number().positive().optional()
        .describe("Font size override in points"),
});

const DimensionOverrideSchema = z.object({
    index: z.number().int().min(1)
        .describe("Row or column index (1-based)"),
    value: z.number().positive()
        .describe("Dimension value in points"),
});

/**
 * Copy table style from another table.
 * Enables pattern detection and style transfer with optional adjustments.
 */
export const CopyStyleFromSchema = z.object({
    sourceSlideId: z.number().int().min(1)
        .describe("Source table's slide number (1-based)"),
    
    sourceShapeId: z.number().int()
        .describe("Source table's shape ID"),
    
    copyWhat: z.array(z.enum(["all", "colors", "fonts", "borders", "alignments", "dimensions"]))
        .default(["all"])
        .describe(`What aspects to copy:
- all: Everything (default)
- colors: Fill colors only
- fonts: Font styling only
- borders: Border styling only
- alignments: Text alignment only
- dimensions: Row heights and column widths only`),
    
    detectPatterns: z.boolean().default(true)
        .describe("Enable pattern detection (header rows, alternating rows, etc.)"),
    
    adjustments: z.object({
        scaleWidth: z.number().positive().optional()
            .describe("Multiply column widths by this factor (e.g., 1.5 = 50% wider)"),
        scaleHeight: z.number().positive().optional()
            .describe("Multiply row heights by this factor"),
        overrideColors: z.array(ColorOverrideSchema).optional()
            .describe("Replace specific colors throughout the styling"),
        overrideFont: FontOverrideSchema.optional()
            .describe("Override font properties globally"),
        columnWidths: z.array(DimensionOverrideSchema).optional()
            .describe("Override specific column widths"),
        rowHeights: z.array(DimensionOverrideSchema).optional()
            .describe("Override specific row heights"),
    }).optional()
        .describe("Adjustments to apply to the copied style"),
}).describe("Copy styling from another table with pattern detection");

export type CopyStyleFrom = z.infer<typeof CopyStyleFromSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// TABLE CONFIG SCHEMA WITH OPERATIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Table config schema with operations (for edit mode)
 */
export const TableConfigWithOperationsSchema = z.object({
    rows: z.number().int().min(1).optional()
        .describe("Number of rows (REQUIRED for create, optional for edit)"),
    
    columns: z.number().int().min(1).optional()
        .describe("Number of columns (REQUIRED for create, optional for edit)"),
    
    rowOperations: z.array(RowOperationSchema).optional()
        .describe("Row operations (edit mode only): remove first, then insert (1-based indices)"),
    
    columnOperations: z.array(ColumnOperationSchema).optional()
        .describe("Column operations (edit mode only): remove first, then insert (1-based indices)"),
    
    columnWidths: z.array(z.number().min(0)).optional()
        .describe("Width of each column (must have 'columns' elements)"),
    
    rowHeights: z.array(z.number().min(0)).optional()
        .describe("Height of each row (must have 'rows' elements)"),
    
    mergeCells: z.array(CellRangeSchema).optional()
        .describe("Cells to merge"),
    
    cellText: z.array(CellTextWithOperationsSchema).optional()
        .describe("Text content for cells (create: include all cells; edit: include changed cells or whole row/column for formatting)"),
    
    cellFormatting: z.array(CellFormattingSchema).optional()
        .describe("Formatting for cell ranges (fills/borders are off by default)"),
    
    copyStyleFrom: CopyStyleFromSchema.optional()
        .describe(`Copy styling from another table. When set, applies detected patterns 
(header row, header column, alternating rows) from the source table to this table.
Useful for making tables match a template's look. Can include color remapping and dimension scaling.`),
});

// ═══════════════════════════════════════════════════════════════════════════
// MAIN CREATE OR EDIT TABLE PARAMS SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create Or Edit Table parameters schema
 */
export const CreateOrEditTableParamsSchema = z.object({
    slideId: z.number().int().min(1)
        .describe("1-based slide index"),
    
    operationType: z.enum(["create", "edit"])
        .describe("'create' for new table, 'edit' for existing table"),
    
    shapeId: z.number().int().optional()
        .describe("Table shape ID (REQUIRED when operationType='edit')"),
    
    purpose: z.string().optional()
        .describe("Documentation: what this table is for"),
    
    notes: z.string().optional()
        .describe("Additional notes"),
    
    // Position & Transform
    position: PositionSchema.optional()
        .describe("Table position and dimensions"),
    
    rotation: z.number().optional()
        .describe("Rotation in degrees clockwise"),
    
    zOrder: z.number().int().optional()
        .describe("Stacking order (higher = on top)"),
    
    // NOTE: Tables don't have shape-level fill/outline/shadow
    // Those are handled by individual cells
    
    // Table config
    tableConfig: TableConfigWithOperationsSchema
        .describe("Table configuration with optional operations"),
    
    // Auto-resize behavior
    autoResize: z.boolean().optional().default(true)
        .describe("When true (default), auto-resize table to fit original bounds when rows/cols added. Set false when space planning indicates room for expansion."),
    
    // Metadata (edit mode)
    metadata: MetadataOperationsSchema.optional()
        .describe("Unified metadata (edit mode: removeEntryIds + addEntries)"),
}).refine(
    (data) => {
        // rows and columns required for create
        if (data.operationType === "create") {
            if (!data.tableConfig.rows || !data.tableConfig.columns) {
                return false;
            }
        }
        // shapeId required for edit
        if (data.operationType === "edit" && !data.shapeId) {
            return false;
        }
        return true;
    },
    {
        message: "rows/columns required for create mode, shapeId required for edit mode",
    }
);

export type CreateOrEditTableParams = z.infer<typeof CreateOrEditTableParamsSchema>;
export type TableConfigWithOperations = z.infer<typeof TableConfigWithOperationsSchema>;
export type RowOperation = z.infer<typeof RowOperationSchema>;
export type ColumnOperation = z.infer<typeof ColumnOperationSchema>;
export type CellTextWithOperations = z.infer<typeof CellTextWithOperationsSchema>;

